import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';
import IdealProfilePanel from './IdealProfilePanel';

export default function BuyerCriteria() {
  const { buyerId } = useParams();
  const { user } = useAuth();

  const [buyer, setBuyer] = useState(null);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Add/Edit state
  const [showAddParent, setShowAddParent] = useState(false);
  const [showAddSub, setShowAddSub] = useState(null); // parent_id or null
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', weight: 0 });
  const [addForm, setAddForm] = useState({ name: '', description: '', weight: 5 });
  const [targets, setTargets] = useState({
    max_lease_rate: '', min_sqft: '', min_ceiling_height: '', min_loading_docks: '', min_parking_ratio: '', target_cap_rate: '',
    target_property_type: ''
  });
  const [savingTargets, setSavingTargets] = useState(false);

  useEffect(() => { loadData(); }, [buyerId]);

  const loadData = async () => {
    try {
      const [buyerRes, criteriaRes, measRes] = await Promise.all([
        authFetch(`${API}/api/buyers/${buyerId}`),
        authFetch(`${API}/api/criteria/buyer/${buyerId}/hierarchy`),
        authFetch(`${API}/api/measurables/definitions/${buyerId}`),
      ]);
      if (buyerRes.ok) setBuyer(await buyerRes.json());
      if (criteriaRes.ok) setHierarchy(await criteriaRes.json());
      if (measRes.ok) {
        const defs = await measRes.json();
        const t = { max_lease_rate: '', min_sqft: '', min_ceiling_height: '', min_loading_docks: '', min_parking_ratio: '', target_cap_rate: '', target_property_type: '' };
        defs.forEach(d => {
          const n = d.name.toLowerCase();
          if (n.includes('lease rate')) t.max_lease_rate = d.target_value || '';
          else if (n.includes('sqft') || n.includes('square')) t.min_sqft = d.target_value || '';
          else if (n.includes('ceiling')) t.min_ceiling_height = d.target_value || '';
          else if (n.includes('loading dock')) t.min_loading_docks = d.target_value || '';
          else if (n.includes('parking')) t.min_parking_ratio = d.target_value || '';
          else if (n.includes('cap rate')) t.target_cap_rate = d.target_value || '';
          else if (n.includes('property type') || n.includes('zoning')) t.target_property_type = d.target_text || '';
        });
        setTargets(t);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => { setSuccess(''); setError(''); };

  const saveOneTarget = async (name, unit, operator, value) => {
    if (value === '' || value === null || value === undefined) return;
    try {
      const existing = await authFetch(`${API}/api/measurables/definitions/${buyerId}`);
      let existingDefs = [];
      if (existing.ok) existingDefs = await existing.json();
      const match = existingDefs.find(d => d.name === name);
      const body = { name, unit, target_operator: operator, target_value: parseFloat(value), buyer_id: parseInt(buyerId), is_active: true, display_order: 0 };
      if (match) {
        await authFetch(`${API}/api/measurables/definitions/${match.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      } else {
        await authFetch(`${API}/api/measurables/definitions`, { method: 'POST', body: JSON.stringify(body) });
      }
    } catch (err) { console.error('Save target error:', err); }
  };

  const saveOneTextTarget = async (name, unit, textValue) => {
    try {
      const existing = await authFetch(`${API}/api/measurables/definitions/${buyerId}`);
      let existingDefs = [];
      if (existing.ok) existingDefs = await existing.json();
      const match = existingDefs.find(d => d.name === name);
      const body = { name, unit, target_text: textValue || '', buyer_id: parseInt(buyerId), is_active: true, display_order: 0 };
      if (match) {
        await authFetch(`${API}/api/measurables/definitions/${match.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      } else if (textValue) {
        // Don't create a row for an empty selection
        await authFetch(`${API}/api/measurables/definitions`, { method: 'POST', body: JSON.stringify(body) });
      }
    } catch (err) { console.error('Save text target error:', err); }
  };

  const triggerRescore = () => {
    clearTimeout(window._rescoreTimer);
    window._rescoreTimer = setTimeout(async () => {
      try {
        await authFetch(`${API}/api/grades/rescore-buyer/${buyerId}`, { method: 'POST' });
      } catch (err) { /* silent */ }
    }, 2000);
  };

  const updateTarget = (field, value, name, unit, operator) => {
    setTargets(prev => ({ ...prev, [field]: value }));
    if (value !== '' && value !== null) {
      clearTimeout(window['_targetTimer_' + field]);
      window['_targetTimer_' + field] = setTimeout(() => {
        saveOneTarget(name, unit, operator, value);
        triggerRescore();
      }, 500);
    }
  };

  const updateTextTarget = (field, value, name, unit) => {
    setTargets(prev => ({ ...prev, [field]: value }));
    saveOneTextTarget(name, unit, value);
    triggerRescore();
  };

  // ─── Weight Updates ───

  const updateWeight = (criterionId, newWeight, isParent = false) => {
    setHierarchy(prev => prev.map(parent => {
      if (isParent && parent.id === criterionId) {
        return { ...parent, weight: newWeight };
      }
      return {
        ...parent,
        sub_criteria: parent.sub_criteria.map(sub =>
          sub.id === criterionId ? { ...sub, weight: newWeight } : sub
        )
      };
    }));
  };

  const saveWeights = async () => {
    setSaving(true);
    clearMessages();
    try {
      const updates = [];
      hierarchy.forEach(parent => {
        updates.push({ id: parent.id, weight: parent.weight });
        parent.sub_criteria.forEach(sub => {
          updates.push({ id: sub.id, weight: sub.weight });
        });
      });
      const res = await authFetch(`${API}/api/criteria/buyer/${buyerId}/bulk-weights`, {
        method: 'PUT',
        body: JSON.stringify({ updates })
      });
      if (res.ok) {
        const data = await res.json();
        setSuccess(`Updated ${data.updated} criteria weights — rescoring properties...`);
        // Rescore all properties for this buyer with new weights
        try {
          const rescoreRes = await authFetch(`${API}/api/grades/rescore-buyer/${buyerId}`, { method: 'POST' });
          if (rescoreRes.ok) {
            const rescoreData = await rescoreRes.json();
            setSuccess(`Updated ${data.updated} criteria weights — ${rescoreData.properties_rescored} properties rescored`);
          }
        } catch (err) { /* silent - weights saved even if rescore fails */ }
      } else {
        setError('Failed to save weights');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Add Criterion ───

  const handleAddParent = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      const res = await authFetch(`${API}/api/criteria`, {
        method: 'POST',
        body: JSON.stringify({
          buyer_id: parseInt(buyerId),
          name: addForm.name,
          description: addForm.description,
          category_type: 'parent',
          weight: Math.round(addForm.weight) * 0.05,
          parent_id: null
        })
      });
      if (res.ok) {
        setSuccess(`Category "${addForm.name}" added`);
        setAddForm({ name: '', description: '', weight: 5 });
        setShowAddParent(false);
        loadData();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to add category');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleAddSub = async (e, parentId) => {
    e.preventDefault();
    clearMessages();
    try {
      const res = await authFetch(`${API}/api/criteria`, {
        method: 'POST',
        body: JSON.stringify({
          buyer_id: parseInt(buyerId),
          name: addForm.name,
          description: addForm.description,
          category_type: 'sub',
          weight: Math.round(addForm.weight) * 0.05,
          parent_id: parentId
        })
      });
      if (res.ok) {
        setSuccess(`Sub-criterion "${addForm.name}" added`);
        setAddForm({ name: '', description: '', weight: 5 });
        setShowAddSub(null);
        loadData();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to add sub-criterion');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  // ─── Edit Criterion ───

  const startEdit = (criterion) => {
    setEditingId(criterion.id);
    setEditForm({ name: criterion.name, description: criterion.description || '', weight: criterion.weight });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', description: '', weight: 0 });
  };

  const handleEdit = async (criterionId) => {
    clearMessages();
    try {
      const res = await authFetch(`${API}/api/criteria/${criterionId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editForm.name,
          description: editForm.description,
          weight: editForm.weight
        })
      });
      if (res.ok) {
        setSuccess('Criterion updated');
        cancelEdit();
        loadData();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to update');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  // ─── Delete Criterion ───

  const handleDelete = async (criterionId, criterionName, isParent) => {
    const msg = isParent
      ? `Delete "${criterionName}" and ALL its sub-criteria? This will also remove any grades for these criteria.`
      : `Delete "${criterionName}"? This will also remove any grades for this criterion.`;
    if (!window.confirm(msg)) return;
    clearMessages();
    try {
      const res = await authFetch(`${API}/api/criteria/${criterionId}`, { method: 'DELETE' });
      if (res.ok) {
        setSuccess(`"${criterionName}" deleted`);
        loadData();
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to delete');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const parentTotal = hierarchy.reduce((sum, p) => sum + (p.weight || 0), 0);

  // CRE: agents, brokers, super_admins, and the client themselves can all view/edit criteria.
  // (RE restricted this to client + super_admin; commercial transactions need agent involvement.)

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading criteria...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-6">
          <div>
            <Link to={user?.role === 'buyer' ? '/' : '/buyers'} className="text-[#00D4FF] text-sm hover:text-[#00E676] transition-colors mb-1 inline-block">
              {user?.role === 'buyer' ? '← Back to Home' : '← Back to Clients'}
            </Link>
            <h1 className="text-xl md:text-2xl font-bold text-white">{buyer?.name}'s Criteria</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">Adjust weights, add or remove evaluation criteria</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setShowAddParent(!showAddParent); setShowAddSub(null); }}
              className="px-4 py-2 bg-[#243B55] text-[#00D4FF] font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors text-sm"
            >
              + Add Category
            </button>
            <button
              onClick={saveWeights}
              disabled={saving}
              className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors text-sm"
            >
              {saving ? 'Saving...' : 'Save Weights'}
            </button>
          </div>
        </div>

        {/* Messages */}
        {success && <div className="bg-green-900/50 border border-green-600 text-green-200 px-4 py-3 rounded-lg mb-4 text-sm">{success}</div>}
        {error && <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}

        {/* Ideal Property Profile — inline AI summary based on current criteria */}
        <div className="mb-6">
          <IdealProfilePanel buyerId={buyerId} />
        </div>

        {/* Parent Weight Total */}
        <div className={`mb-6 p-4 rounded-lg border ${
          Math.abs(parentTotal - 1.0) < 0.01 ? 'bg-green-900/20 border-green-600/30' : 'bg-yellow-900/20 border-yellow-600/30'
        }`}>
          <div className="flex flex-wrap justify-between items-center gap-x-2 gap-y-1">
            <span className="text-sm text-gray-300">Parent category weights total:</span>
            <span className={`text-lg font-bold ${Math.abs(parentTotal - 1.0) < 0.01 ? 'text-[#00E676]' : 'text-yellow-400'}`}>
              {(parentTotal * 100).toFixed(0)}%
              {Math.abs(parentTotal - 1.0) >= 0.01 && ' (should be 100%)'}
            </span>
          </div>
        </div>

        {/* Add Parent Form */}
        {showAddParent && (
          <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/30 p-5 mb-4">
            <h3 className="text-white font-semibold mb-3">New Parent Category</h3>
            <form onSubmit={handleAddParent} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Name *</label>
                <input type="text" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="e.g., Investment Potential" required />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Description</label>
                <input type="text" value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                  placeholder="Rental income, appreciation potential" />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-gray-400 mb-1">Importance (1-9)</label>
                  <input type="number" step="1" min="1" max="9" value={addForm.weight}
                    onChange={(e) => setAddForm({ ...addForm, weight: parseInt(e.target.value) || 5 })}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                </div>
                <button type="submit" className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg text-sm hover:bg-[#00C853]">Add</button>
                <button type="button" onClick={() => setShowAddParent(false)} className="px-3 py-2 text-gray-400 hover:text-white text-sm">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Buyer Targets */}
        <div className="bg-[#1A2744] rounded-xl border border-[#00D4FF]/20 p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 md:gap-0 mb-4">
            <div>
              <h2 className="text-white font-semibold text-lg">Client Targets</h2>
              <p className="text-gray-400 text-sm">Set your client's must-haves. The auto-scorer uses these to grade properties more precisely.</p>
            </div>
<span className="text-gray-500 text-xs">Auto-saves on change</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Max Lease Rate ($/sqft/yr)</label>
              <input type="number" step="0.01" value={targets.max_lease_rate} onChange={(e) => updateTarget("max_lease_rate", e.target.value, "Max Lease Rate", "$/sqft/yr", "<=")}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="28.50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Min Square Footage (sqft)</label>
              <input type="number" value={targets.min_sqft} onChange={(e) => updateTarget("min_sqft", e.target.value, "Min Square Footage", "sqft", ">=")}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="10000" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Min Ceiling Height (ft)</label>
              <input type="number" step="0.5" value={targets.min_ceiling_height} onChange={(e) => updateTarget("min_ceiling_height", e.target.value, "Min Ceiling Height", "ft", ">=")}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="14" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Min Loading Docks</label>
              <input type="number" value={targets.min_loading_docks} onChange={(e) => updateTarget("min_loading_docks", e.target.value, "Min Loading Docks", "docks", ">=")}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="2" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Min Parking Ratio (per 1000 sqft)</label>
              <input type="number" step="0.1" value={targets.min_parking_ratio} onChange={(e) => updateTarget("min_parking_ratio", e.target.value, "Min Parking Ratio", "per 1000 sqft", ">=")}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="3.5" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Target Cap Rate (%)</label>
              <input type="number" step="0.1" value={targets.target_cap_rate} onChange={(e) => updateTarget("target_cap_rate", e.target.value, "Target Cap Rate", "%", ">=")}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="6.5" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Target Property Type</label>
              <select value={targets.target_property_type} onChange={(e) => updateTextTarget("target_property_type", e.target.value, "Target Property Type", "")}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                <option value="">— Any —</option>
                <option value="office">Office</option>
                <option value="retail">Retail</option>
                <option value="industrial">Industrial</option>
                <option value="warehouse">Warehouse</option>
                <option value="flex">Flex</option>
                <option value="mixed_use">Mixed Use</option>
                <option value="land">Land</option>
              </select>
            </div>
          </div>
        </div>

        {/* Criteria Hierarchy */}
        <div className="space-y-4">
          {hierarchy.map(parent => (
            <div key={parent.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
              {/* Parent Category */}
              <div className="p-4 md:p-5 border-b border-[#546E7A]/20">
                {editingId === parent.id ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                    <input type="text" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(parent.id)} className="px-3 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg text-sm">Save</button>
                      <button onClick={cancelEdit} className="px-3 py-2 text-gray-400 hover:text-white text-sm">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold text-lg break-words">{parent.name}</h3>
                        <p className="text-gray-400 text-sm break-words">{parent.description}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-[#00D4FF] font-bold text-xl">{(parent.weight * 100).toFixed(0)}%</div>
                        <button onClick={() => startEdit(parent)} className="text-gray-500 hover:text-[#00D4FF] text-sm transition-colors" title="Edit">✎</button>
                        <button onClick={() => handleDelete(parent.id, parent.name, true)} className="text-gray-600 hover:text-red-400 text-sm transition-colors" title="Delete">✕</button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-8">0%</span>
                      <input type="range" min="0" max="1" step="0.05" value={parent.weight}
                        onChange={(e) => updateWeight(parent.id, parseFloat(e.target.value), true)}
                        className="flex-1 accent-[#00D4FF] h-2" />
                      <span className="text-xs text-gray-500 w-12">100%</span>
                    </div>
                  </>
                )}
              </div>

              {/* Sub-Criteria */}
              <div className="divide-y divide-[#546E7A]/10">
                {parent.sub_criteria.map(sub => (
                  <div key={sub.id} className="px-5 py-3">
                    {editingId === sub.id ? (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                        <input type="text" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                          placeholder="Description" />
                        <input type="number" step="0.05" min="0" max="1" value={editForm.weight}
                          onChange={(e) => setEditForm({ ...editForm, weight: parseFloat(e.target.value) || 0 })}
                          className="px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                        <div className="flex gap-2">
                          <button onClick={() => handleEdit(sub.id)} className="px-3 py-1.5 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg text-sm">Save</button>
                          <button onClick={cancelEdit} className="px-3 py-1.5 text-gray-400 hover:text-white text-sm">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-200 text-sm font-medium break-words min-w-0">{sub.name}</span>
                            <button onClick={() => startEdit(sub)} className="text-gray-600 hover:text-[#00D4FF] text-xs transition-colors shrink-0" title="Edit">✎</button>
                            <button onClick={() => handleDelete(sub.id, sub.name, false)} className="text-gray-700 hover:text-red-400 text-xs transition-colors shrink-0" title="Delete">✕</button>
                          </div>
                          <div className="text-gray-500 text-xs break-words">{sub.description}</div>
                        </div>
                        <div className="flex items-center gap-3 md:shrink-0 w-full md:w-64">
                          <input type="range" min="0" max="1" step="0.05" value={sub.weight}
                            onChange={(e) => updateWeight(sub.id, parseFloat(e.target.value))}
                            className="flex-1 accent-[#00D4FF] h-1.5" />
                          <span className="text-[#00D4FF] font-medium text-sm w-10 text-right">
                            {(sub.weight * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add Sub-Criterion */}
                {showAddSub === parent.id ? (
                  <div className="px-5 py-3 bg-[#0A0E27]/50">
                    <form onSubmit={(e) => handleAddSub(e, parent.id)} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <input type="text" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                        className="px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                        placeholder="Criterion name" required />
                      <input type="text" value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                        className="px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                        placeholder="Description" />
                      <input type="number" step="1" min="1" max="9" value={addForm.weight}
                        onChange={(e) => setAddForm({ ...addForm, weight: parseInt(e.target.value) || 5 })}
                        className="px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                        placeholder="1-9" />
                      <div className="flex gap-2">
                        <button type="submit" className="px-3 py-1.5 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg text-sm">Add</button>
                        <button type="button" onClick={() => setShowAddSub(null)} className="px-3 py-1.5 text-gray-400 hover:text-white text-sm">Cancel</button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="px-5 py-2">
                    <button
                      onClick={() => { setShowAddSub(parent.id); setShowAddParent(false); setAddForm({ name: '', description: '', weight: 5 }); }}
                      className="text-gray-500 hover:text-[#00D4FF] text-xs transition-colors"
                    >
                      + Add sub-criterion
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Save Weights Bottom */}
        <div className="mt-6 flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0">
          <Link to={user?.role === 'buyer' ? '/' : '/buyers'} className="text-gray-400 hover:text-white text-sm transition-colors">
            {user?.role === 'buyer' ? '← Back to Home' : '← Back to Clients'}
          </Link>
          <button
            onClick={saveWeights}
            disabled={saving}
            className="px-6 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save All Weights'}
          </button>
        </div>
      </div>
    </div>
  );
}
